import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  ROUTE_OPTIONS,
  administrableBatches,
  canAdminister,
  mayProceedToAdministration,
  parseScreeningItems,
  pickTemplateForAge,
  suggestConclusionLocal,
  vaccinationApi,
  vaccinationQueryKeys,
  vaxConclusionLabel,
  vaxDueStatusLabel,
  type VaxRecord,
} from "../../shared/api/vaccination";
import { patientsApi } from "../../shared/api/patients";
import { inventoryApi, inventoryQueryKeys } from "../../shared/api/inventory";
import { apiError } from "../../shared/api/client";
import { formatDate, shiftIsoDate } from "../../shared/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Picked = { id: string; name: string; code?: string | null; dob?: string | null };

// shadcn Select cannot use an empty-string item value; map "" ↔ sentinel.
const NO_PROTOCOL = "__none__";

/**
 * Tiêm chủng · Khám &amp; Tiêm (P0A golden loop, UI). Guided flow: chọn bệnh nhân → sàng lọc →
 * ĐỦ ĐK → chỉ định → chọn lô thực tế → tiêm → sổ tiêm + mũi kế tiếp. Mỗi bước gọi đúng API P0A;
 * tồn kho trừ đúng lô khi tiêm thành công.
 */
export function VaccinationWorkspacePage() {
  const qc = useQueryClient();
  const [patient, setPatient] = useState<Picked | null>(null);
  const [protocolId, setProtocolId] = useState("");
  const [screeningId, setScreeningId] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [record, setRecord] = useState<VaxRecord | null>(null);

  // Preselect from a booking hand-off ("Bắt đầu tiêm" → ?patientId=&protocolId=).
  const [searchParams] = useSearchParams();
  const prePatientId = searchParams.get("patientId");
  const preProtocolId = searchParams.get("protocolId");
  const preload = useQuery({
    queryKey: ["patients", "get", prePatientId],
    queryFn: () => patientsApi.get(prePatientId!),
    enabled: !!prePatientId && !patient,
  });
  useEffect(() => {
    if (preload.data && !patient) {
      setPatient({
        id: preload.data.id,
        name: preload.data.fullName,
        code: preload.data.patientCode,
        dob: preload.data.dateOfBirth,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preload.data]);
  useEffect(() => {
    if (preProtocolId && !protocolId) setProtocolId(preProtocolId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preProtocolId]);

  const protocols = useQuery({
    queryKey: vaccinationQueryKeys.protocols(),
    queryFn: () => vaccinationApi.listProtocols(),
  });
  const protocol = protocols.data?.find((p) => p.id === protocolId) ?? null;

  // Full reset — a different patient means a new visit + a new screening.
  function resetVisit() {
    setProtocolId("");
    setScreeningId(null);
    setOrderId(null);
    setRecord(null);
  }
  // Same patient, same visit: give ANOTHER vaccine reusing the one screening
  // (a single pre-vaccination health check covers all jabs given today).
  function nextVaccine() {
    setProtocolId("");
    setOrderId(null);
    setRecord(null);
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-1">
        <h1 className="text-xl font-bold text-foreground">
          Khám sàng lọc &amp; Tiêm chủng
        </h1>
        <p className="text-sm text-muted-foreground">
          Chọn bệnh nhân → sàng lọc → chỉ định → chọn lô → tiêm → sổ tiêm &amp; mũi kế tiếp.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <PatientPicker
            patient={patient}
            onPick={(p) => {
              setPatient(p);
              resetVisit();
            }}
          />

          {patient && (
            <Card className="mt-3">
              <CardContent className="p-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="protocol">Phác đồ</Label>
                  <Select
                    value={protocolId === "" ? NO_PROTOCOL : protocolId}
                    onValueChange={(v) => {
                      // Keep the visit's screening; only reset the per-vaccine order/record.
                      setProtocolId(v === NO_PROTOCOL ? "" : v);
                      setOrderId(null);
                      setRecord(null);
                    }}
                  >
                    <SelectTrigger id="protocol" aria-label="Phác đồ">
                      <SelectValue placeholder="— Chọn phác đồ —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_PROTOCOL}>— Chọn phác đồ —</SelectItem>
                      {protocols.data?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {protocol && !record && (
                  <>
                    {!screeningId && (
                      <ScreeningStep
                        patientId={patient.id}
                        patientDob={patient.dob ?? null}
                        protocolId={protocol.id}
                        onDone={setScreeningId}
                      />
                    )}
                    {screeningId && !orderId && (
                      <OrderStep
                        screeningId={screeningId}
                        protocolId={protocol.id}
                        patientId={patient.id}
                        onDone={setOrderId}
                      />
                    )}
                    {orderId && (
                      <AdministerStep
                        orderId={orderId}
                        vaccineMedicineId={protocol.vaccineMedicineId}
                        onDone={(rec) => {
                          setRecord(rec);
                          qc.invalidateQueries({
                            queryKey: vaccinationQueryKeys.history(patient.id),
                          });
                          qc.invalidateQueries({
                            queryKey: vaccinationQueryKeys.nextDue(patient.id, protocol.id),
                          });
                        }}
                      />
                    )}
                  </>
                )}

                {record && (
                  <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                    ✓ Đã tiêm mũi {record.doseNo} — lô {record.lotNumber ?? "—"} (HSD{" "}
                    {record.expiryDate ?? "—"}). Đã trừ kho.
                    <PostAdminActions recordId={record.id} onReset={nextVaccine} />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          {patient && protocol && (
            <NextDueCard patientId={patient.id} protocolId={protocol.id} />
          )}
          {patient && <HistoryCard patientId={patient.id} />}
        </div>
      </div>
    </div>
  );
}

function PatientPicker({
  patient,
  onPick,
}: {
  patient: Picked | null;
  onPick: (p: Picked) => void;
}) {
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["patients", "search", q],
    queryFn: () => patientsApi.search(q, { page: 0, size: 8 }),
    enabled: q.trim().length >= 2,
  });
  return (
    <Card>
      <CardContent className="p-4">
        <div className="grid gap-1.5">
          <Label htmlFor="patient-search">
            Bệnh nhân {patient ? `— đang chọn: ${patient.name}` : ""}
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="patient-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Tìm theo tên / SĐT / mã…"
              className="pl-9"
            />
          </div>
        </div>
        {q.trim().length >= 2 && (
          <ul className="mt-1 list-none p-0">
            {search.data?.content.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="w-full cursor-pointer rounded-sm py-1.5 text-left text-sm text-foreground hover:bg-muted"
                  onClick={() =>
                    onPick({
                      id: p.id,
                      name: p.fullName,
                      code: p.patientCode,
                      dob: p.dateOfBirth,
                    })
                  }
                >
                  {p.fullName}
                  {p.patientCode ? ` · ${p.patientCode}` : ""}
                  {p.dateOfBirth ? ` · ${formatDate(p.dateOfBirth)}` : ""}
                </button>
              </li>
            ))}
            {(search.data?.content.length ?? 0) === 0 && !search.isLoading && (
              <li className="py-1.5 text-sm text-muted-foreground">Không tìm thấy.</li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ScreeningStep({
  patientId,
  patientDob,
  protocolId,
  onDone,
}: {
  patientId: string;
  patientDob: string | null;
  protocolId: string;
  onDone: (id: string) => void;
}) {
  const [weight, setWeight] = useState("");
  const [temp, setTemp] = useState("");
  // Item numbers ticked "Bất thường" (Có).
  const [abnormal, setAbnormal] = useState<Set<number>>(new Set());
  // null = follow the auto-suggested conclusion; set = clinician override.
  const [manual, setManual] = useState<
    "ELIGIBLE" | "DEFER" | "CONTRAINDICATED" | "REFER" | null
  >(null);
  const [err, setErr] = useState<string | null>(null);
  const [deferredMsg, setDeferredMsg] = useState<string | null>(null);
  // +7 ngày theo lịch địa phương (#19847), không phải +7×24h rồi lấy ngày UTC.
  const defaultReeval = shiftIsoDate(new Date(), 7);
  const [reevaluate, setReevaluate] = useState(defaultReeval);

  const templates = useQuery({
    queryKey: vaccinationQueryKeys.templates(),
    queryFn: () => vaccinationApi.listTemplates(),
  });
  const nextDue = useQuery({
    queryKey: vaccinationQueryKeys.nextDue(patientId, protocolId),
    queryFn: () => vaccinationApi.nextDue(patientId, protocolId),
  });

  const ageDays = patientDob
    ? Math.floor((Date.now() - new Date(patientDob).getTime()) / 86400000)
    : null;
  const template = pickTemplateForAge(templates.data ?? [], ageDays);
  const items = parseScreeningItems(template?.items);
  const suggested = suggestConclusionLocal(items, abnormal);
  const conclusion = manual ?? suggested;

  function toggle(no: number) {
    setAbnormal((prev) => {
      const s = new Set(prev);
      if (s.has(no)) s.delete(no);
      else s.add(no);
      return s;
    });
  }

  const save = useMutation({
    mutationFn: async () => {
      const answers = Object.fromEntries([...abnormal].map((no) => [no, true]));
      const r = await vaccinationApi.createScreening({
        patientId,
        protocolId,
        templateId: template?.id ?? null,
        headerJson: JSON.stringify({
          weight: weight ? Number(weight) : null,
          temperature: temp ? Number(temp) : null,
        }),
        answersJson: JSON.stringify(answers),
        conclusion,
        referReason: null,
      });
      if (!mayProceedToAdministration(conclusion)) {
        await vaccinationApi.recordDeferral({
          screeningId: r.id,
          patientId,
          protocolId,
          doseNo: nextDue.data?.doseNo ?? null,
          kind: conclusion,
          reason: vaxConclusionLabel[conclusion],
          reevaluateDate: conclusion === "DEFER" ? reevaluate : null,
        });
      }
      return r;
    },
    onSuccess: (r) => {
      if (mayProceedToAdministration(conclusion)) onDone(r.id);
      else
        setDeferredMsg(
          conclusion === "DEFER"
            ? `Đã tạm hoãn — hẹn đánh giá lại ${reevaluate}.`
            : `Đã ghi ${vaxConclusionLabel[conclusion]}. Không tiêm lần này.`,
        );
    },
    onError: (e) => setErr(apiError(e).detail),
  });

  return (
    <div className="mt-3 border-t pt-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-foreground">1 · Khám sàng lọc</h4>
        {template && (
          <Badge variant="secondary" className="font-normal">
            {template.title}
          </Badge>
        )}
      </div>

      {/* Vitals */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="screen-weight">Cân nặng (kg)</Label>
          <Input
            id="screen-weight"
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="screen-temp">Thân nhiệt (°C)</Label>
          <Input
            id="screen-temp"
            type="number"
            value={temp}
            onChange={(e) => setTemp(e.target.value)}
          />
        </div>
      </div>

      {/* Checklist per template — tick "Bất thường" (Có) on any abnormal finding */}
      {items.length > 0 ? (
        <div className="mt-3 rounded-md border">
          <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
            <span>Nội dung khám sàng lọc</span>
            <span>Bất thường (Có)</span>
          </div>
          <ul className="divide-y">
            {items.map((it) => (
              <li key={it.no} className="flex items-start gap-3 px-3 py-2">
                <span className="min-w-0 flex-1 text-sm">
                  <span className="text-muted-foreground">{it.no}.</span> {it.label}
                  <span
                    className={`ml-1.5 text-xs ${
                      it.onAbnormal === "CONTRAINDICATE" ? "text-destructive" : "text-amber-600"
                    }`}
                  >
                    ({it.onAbnormal === "CONTRAINDICATE" ? "chống chỉ định" : "tạm hoãn"})
                  </span>
                </span>
                <Checkbox
                  className="mt-0.5"
                  checked={abnormal.has(it.no)}
                  onCheckedChange={() => toggle(it.no)}
                  aria-label={`Bất thường: ${it.label}`}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          Chưa cấu hình mẫu sàng lọc cho nhóm tuổi này — vào Cài đặt để thêm mẫu.
        </p>
      )}

      {/* Suggested + overridable conclusion */}
      <div className="mt-3 grid gap-1.5 sm:max-w-xs">
        <Label htmlFor="screen-conclusion">
          Kết luận{" "}
          {manual === null && items.length > 0 && (
            <span className="font-normal text-muted-foreground">· gợi ý tự động</span>
          )}
        </Label>
        <Select value={conclusion} onValueChange={(v) => setManual(v as never)}>
          <SelectTrigger id="screen-conclusion" aria-label="Kết luận">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(vaxConclusionLabel).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {conclusion === "DEFER" && (
        <div className="mt-3 grid gap-1.5">
          <Label htmlFor="screen-reeval">Hẹn đánh giá lại</Label>
          <Input
            id="screen-reeval"
            type="date"
            value={reevaluate}
            onChange={(e) => setReevaluate(e.target.value)}
            className="w-auto"
          />
        </div>
      )}
      {err && <p className="mt-3 text-sm text-destructive">{err}</p>}
      {deferredMsg && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {deferredMsg}
        </div>
      )}
      <Button
        type="button"
        className="mt-3"
        disabled={save.isPending || !!deferredMsg}
        onClick={() => save.mutate()}
      >
        {conclusion === "ELIGIBLE" ? "Lưu sàng lọc" : "Ghi kết luận"}
      </Button>
    </div>
  );
}

function OrderStep({
  screeningId,
  protocolId,
  patientId,
  onDone,
}: {
  screeningId: string;
  protocolId: string;
  patientId: string;
  onDone: (id: string) => void;
}) {
  const nextDue = useQuery({
    queryKey: vaccinationQueryKeys.nextDue(patientId, protocolId),
    queryFn: () => vaccinationApi.nextDue(patientId, protocolId),
  });
  const [doseNo, setDoseNo] = useState<number>(1);
  const [route, setRoute] = useState("INJECTION_IM");
  const [err, setErr] = useState<string | null>(null);
  // Prefill from the computed next dose.
  useEffect(() => {
    if (nextDue.data?.doseNo) setDoseNo(nextDue.data.doseNo);
  }, [nextDue.data]);
  // #20019: a "Mũi số" off the patient's history is refused with `dose_number_mismatch`; it is
  // saved only after the operator ticks the confirmation below (and is audited).
  const [mismatch, setMismatch] = useState<string | null>(null);
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [mismatchReason, setMismatchReason] = useState("");
  const save = useMutation({
    mutationFn: () =>
      vaccinationApi.createOrder({
        screeningId,
        protocolId,
        doseNo,
        route,
        ...(mismatch
          ? { doseNoOverride: true, doseNoOverrideReason: mismatchReason.trim() || null }
          : {}),
      }),
    onSuccess: (r) => onDone(r.id),
    onError: (e) => {
      const problem = apiError(e);
      if (problem.code === "dose_number_mismatch") {
        setMismatch(problem.detail);
        setErr(null);
      } else {
        setErr(problem.detail);
      }
    },
  });
  return (
    <div className="mt-3 border-t pt-3">
      <h4 className="mb-2 text-sm font-semibold text-foreground">2 · Chỉ định vắc xin</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="order-dose">Mũi số</Label>
          <Input
            id="order-dose"
            type="number"
            min={1}
            value={doseNo}
            onChange={(e) => {
              setDoseNo(Number(e.target.value) || 1);
              // A new number is a new question: the old warning no longer applies to it.
              setMismatch(null);
              setConfirmMismatch(false);
            }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="order-route">Đường dùng</Label>
          <Select value={route} onValueChange={setRoute}>
            <SelectTrigger id="order-route" aria-label="Đường dùng">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROUTE_OPTIONS.map((r) => (
                <SelectItem key={r.code} value={r.code}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {mismatch && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <p className="font-semibold">{mismatch}</p>
          <p className="mt-1">
            Nếu các mũi trước đã tiêm ở nơi khác, nên nhập bổ sung vào sổ tiêm trước.
          </p>
          <div className="mt-2 grid gap-1.5">
            <Label htmlFor="order-mismatch-reason">Lý do (nếu có)</Label>
            <Input
              id="order-mismatch-reason"
              value={mismatchReason}
              maxLength={500}
              onChange={(e) => setMismatchReason(e.target.value)}
            />
          </div>
          <label className="mt-2 flex items-start gap-2">
            <Checkbox
              className="mt-0.5"
              checked={confirmMismatch}
              onCheckedChange={(v) => setConfirmMismatch(v === true)}
              aria-label="Xác nhận mũi số khác lịch sử tiêm"
            />
            <span>Tôi xác nhận chỉ định Mũi {doseNo} dù khác lịch sử tiêm.</span>
          </label>
        </div>
      )}
      {err && <p className="mt-3 text-sm text-destructive">{err}</p>}
      <Button
        type="button"
        className="mt-3"
        disabled={save.isPending || (mismatch !== null && !confirmMismatch)}
        onClick={() => save.mutate()}
      >
        Tạo chỉ định
      </Button>
    </div>
  );
}

function AdministerStep({
  orderId,
  vaccineMedicineId,
  onDone,
}: {
  orderId: string;
  vaccineMedicineId: string;
  onDone: (rec: VaxRecord) => void;
}) {
  const stock = useQuery({
    queryKey: inventoryQueryKeys.stock(),
    queryFn: () => inventoryApi.stock(),
  });
  const batches = administrableBatches(stock.data ?? [], vaccineMedicineId);
  const [batchId, setBatchId] = useState("");
  const [site, setSite] = useState("Bắp tay trái");
  const [err, setErr] = useState<string | null>(null);
  // #20016: the server refuses a dose before the protocol's earliest date with `dose_too_early`;
  // it goes through only after the operator ticks the confirmation below (and is audited).
  const [early, setEarly] = useState<string | null>(null);
  const [confirmEarly, setConfirmEarly] = useState(false);
  const [earlyReason, setEarlyReason] = useState("");
  const administer = useMutation({
    mutationFn: () =>
      vaccinationApi.administer(orderId, {
        batchId,
        quantity: 1,
        site,
        ...(early ? { earlyOverride: true, earlyOverrideReason: earlyReason.trim() || null } : {}),
      }),
    onSuccess: onDone,
    onError: (e) => {
      const problem = apiError(e);
      if (problem.code === "dose_too_early") {
        setEarly(problem.detail);
        setErr(null);
      } else {
        setErr(problem.detail);
      }
    },
  });
  return (
    <div className="mt-3 border-t pt-3">
      <h4 className="mb-2 text-sm font-semibold text-foreground">3 · Chọn lô &amp; Tiêm</h4>
      {batches.length === 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Không có lô vắc xin còn tồn. Nhập kho lô ở “Điều chỉnh tồn kho”.
        </div>
      ) : (
        <div className="grid gap-1.5">
          <Label htmlFor="admin-batch">Lô thực tế</Label>
          <Select value={batchId} onValueChange={setBatchId}>
            <SelectTrigger id="admin-batch" aria-label="Lô thực tế">
              <SelectValue placeholder="— Chọn lô —" />
            </SelectTrigger>
            <SelectContent>
              {batches.map((b) => (
                <SelectItem key={b.batchId} value={b.batchId}>
                  {b.lotNumber} · HSD {b.expiryDate} · còn {b.quantityOnHand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="mt-3 grid gap-1.5">
        <Label htmlFor="admin-site">Vị trí tiêm</Label>
        <Input id="admin-site" value={site} onChange={(e) => setSite(e.target.value)} />
      </div>
      {early && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <p className="font-semibold">{early}</p>
          <p className="mt-1">Tiêm sớm hơn lịch có thể làm giảm hiệu quả miễn dịch.</p>
          <div className="mt-2 grid gap-1.5">
            <Label htmlFor="admin-early-reason">Lý do tiêm sớm (nếu có)</Label>
            <Input
              id="admin-early-reason"
              value={earlyReason}
              maxLength={500}
              onChange={(e) => setEarlyReason(e.target.value)}
            />
          </div>
          <label className="mt-2 flex items-start gap-2">
            <Checkbox
              className="mt-0.5"
              checked={confirmEarly}
              onCheckedChange={(v) => setConfirmEarly(v === true)}
              aria-label="Xác nhận tiêm sớm hơn lịch"
            />
            <span>Tôi xác nhận tiêm mũi này sớm hơn lịch phác đồ.</span>
          </label>
        </div>
      )}
      {err && <p className="mt-3 text-sm text-destructive">{err}</p>}
      <Button
        type="button"
        className="mt-3"
        disabled={
          !canAdminister(batchId, administer.isPending) || (early !== null && !confirmEarly)
        }
        onClick={() => administer.mutate()}
      >
        {early ? "Xác nhận tiêm sớm" : "Xác nhận tiêm"}
      </Button>
    </div>
  );
}

function PostAdminActions({ recordId, onReset }: { recordId: string; onReset: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const invoice = useMutation({
    mutationFn: () => vaccinationApi.createInvoice(recordId),
    onSuccess: (r) => setMsg(`Đã xuất hoá đơn ${r.code} — ${r.subtotal.toLocaleString("vi-VN")}₫.`),
    onError: (e) => setMsg(apiError(e).detail),
  });
  return (
    <div className="mt-2">
      <div className="mb-1.5 text-xs text-muted-foreground">
        Đã tự đưa vào bảng theo dõi 30′.
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/vaccination/observation">Xem bảng theo dõi</Link>
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={invoice.isPending}
          onClick={() => invoice.mutate()}
        >
          Xuất hoá đơn
        </Button>
        <Button type="button" variant="secondary" onClick={onReset}>
          Tiêm vắc xin khác
        </Button>
      </div>
      {msg && <div className="mt-1.5 text-sm text-foreground">{msg}</div>}
    </div>
  );
}

function NextDueCard({ patientId, protocolId }: { patientId: string; protocolId: string }) {
  const q = useQuery({
    queryKey: vaccinationQueryKeys.nextDue(patientId, protocolId),
    queryFn: () => vaccinationApi.nextDue(patientId, protocolId),
  });
  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        <h3 className="mb-2 text-base font-semibold text-foreground">Mũi kế tiếp</h3>
        {q.isLoading && <p className="text-sm text-muted-foreground">Đang tính…</p>}
        {q.data?.seriesComplete && (
          <p className="text-sm text-foreground">✓ Đã hoàn thành phác đồ.</p>
        )}
        {q.data && !q.data.seriesComplete && (
          <ul className="list-none space-y-1.5 p-0 text-sm">
            <li className="flex items-center gap-2">
              <b>Mũi {q.data.doseNo}</b>
              <Badge variant="secondary">
                {vaxDueStatusLabel[q.data.status ?? ""] ?? q.data.status}
              </Badge>
            </li>
            <li>Sớm nhất: {q.data.earliest ?? "—"}</li>
            <li>Khuyến nghị: {q.data.recommended ?? "—"}</li>
            <li className="text-xs text-muted-foreground">{q.data.reason}</li>
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryCard({ patientId }: { patientId: string }) {
  const q = useQuery({
    queryKey: vaccinationQueryKeys.history(patientId),
    queryFn: () => vaccinationApi.history(patientId),
  });
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-2 text-base font-semibold text-foreground">Sổ tiêm</h3>
        {(q.data?.length ?? 0) === 0 && (
          <p className="text-sm text-muted-foreground">Chưa có mũi nào.</p>
        )}
        <ul className="list-none p-0 text-sm">
          {q.data?.map((r, i) => (
            <li key={r.id}>
              {i > 0 && <Separator className="my-1.5" />}
              <b>Mũi {r.doseNo}</b> · {formatDate(r.administeredAt)} · lô {r.lotNumber ?? "—"}
              {r.source === "EXTERNAL" ? " · (ngoại viện)" : ""}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
